// Auth state for the portal.
//
// • login(): POST /admin/auth/login, then refuse anyone who isn't a
//   super-admin (the token is discarded, never stored).
// • adoptSession(): the invite page's session (sign in + claim, or accept),
//   stored only when it belongs to a super-admin.
// • A stored session is used at once, then revalidated with GET /auth/me.
// • Any 401 from any request → signed out with reason "expired"; the route
//   guard sends the user to /login?next=<where they were>.
// • The token's own `exp` also signs the user out on time, without waiting
//   for a request to fail.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { api } from '@/api'
import { ApiError } from '@/api/client'
import type { Me } from '@/api/types'
import { clearQueryCache } from '@/lib/query'
import { AuthContext, NOT_SUPER_ADMIN_MESSAGE, type SignOutReason } from './useAuth'
import { clearSession, onSessionExpired, readSession, readToken, tokenExpiry, writeSession } from './session'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Me | null>(() => {
    const s = readSession()
    if (!s) clearSession()
    return s?.user ?? null
  })
  const [signOutReason, setSignOutReason] = useState<SignOutReason>(null)

  const userRef = useRef(user)
  userRef.current = user

  const logout = useCallback((reason: Exclude<SignOutReason, null> = 'signed_out') => {
    clearSession()
    clearQueryCache()
    // Only report a reason if someone was actually signed in. A late 401 that
    // lands after the first logout keeps the reason it set ("expired").
    setSignOutReason((prev) => (userRef.current ? reason : prev))
    setUser(null)
  }, [])

  // Any 401 anywhere → signed out.
  useEffect(() => onSessionExpired(() => logout('expired')), [logout])

  // Sign out when the token's exp passes (setTimeout caps at ~24.8 days).
  useEffect(() => {
    if (!user) return
    const token = readToken()
    const exp = token ? tokenExpiry(token) : null
    if (exp === null) return
    const ms = Math.min(exp - Date.now(), 2 ** 31 - 1)
    const t = setTimeout(() => logout('expired'), Math.max(0, ms))
    return () => clearTimeout(t)
  }, [user, logout])

  // Revalidate a restored session once (deactivated or demoted users).
  const userId = user?.id
  useEffect(() => {
    if (!userId) return
    const controller = new AbortController()
    api.auth
      .me(controller.signal)
      .then((me) => {
        if (me.platform_role !== 'super_admin') logout('signed_out')
      })
      .catch(() => {
        /* 401 is handled globally; network errors keep the session */
      })
    return () => controller.abort()
  }, [userId, logout])

  const login = useCallback(async (email: string, password: string) => {
    const { token, user: me } = await api.auth.login(email, password)
    if (me.platform_role !== 'super_admin') {
      throw new ApiError({ status: 403, code: 'not_super_admin', message: NOT_SUPER_ADMIN_MESSAGE })
    }
    writeSession(token, me)
    setSignOutReason(null)
    setUser(me)
    return me
  }, [])

  const adoptSession = useCallback((token: string, me: Me) => {
    if (me.platform_role !== 'super_admin') {
      throw new ApiError({ status: 403, code: 'not_super_admin', message: NOT_SUPER_ADMIN_MESSAGE })
    }
    writeSession(token, me)
    setSignOutReason(null)
    setUser(me)
  }, [])

  const value = useMemo(() => ({ user, signOutReason, login, adoptSession, logout }), [user, signOutReason, login, adoptSession, logout])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
